/* eslint-disable camelcase */
// One dialog for both kinds of report schedule.
//
// Creating a schedule used to mean answering a question first — CRM report or
// client advertising report? — and then meeting one of two forms that shared
// almost nothing. Here the report type is simply the first field, both
// families in one list, and everything under it is in the same place whichever
// type is chosen: when it runs, then a middle section that swaps to that
// type's own settings, then who receives it, then whether it is live. Putting
// the cadence directly under the type keeps the delivery day and time on
// screen without scrolling for both types, including the one that carries
// more settings of its own.
//
// The two families still post their own payloads to their own endpoints.
// `scheduleDialogModel` owns that translation; nothing here knows a URL.
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    ListSubheader,
    MenuItem,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    alpha,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Iconify } from 'src/components';
import { reportRequest } from '../clientReports/clientReportApi';
import ClientReportPreview from '../clientReports/ClientReportPreview';
import TimezoneSelect, { DEFAULT_TIMEZONE, isValidTimezone } from './TimezoneSelect';
import { reportingFocusVisibleSx } from './focusVisible';
import ScheduleClientFields from './ScheduleClientFields';
import { needsPlatforms } from '../clientReports/clientReportModel';
import ScheduleAdvancedFields from './ScheduleAdvancedFields';
import {
    CLIENT,
    LEGACY,
    DAYS_OF_WEEK,
    EMAIL_PATTERN,
    MONTHLY_INTERVAL,
    WEEKLY_INTERVAL,
    cleanRecipients,
    defaultEnabled,
    emptyScheduleForm,
    frequencyOptionsFor,
    hasAdvancedValues,
    kindOf,
    scheduleIssues,
    scheduleTypeGroups,
} from './scheduleDialogModel';

// Which locations this viewer may report on, for the platforms they chose.
export const fetchAuthorisedLocations = (platformsKey, signal, reportType = '') =>
    reportRequest(
        needsPlatforms(reportType)
            ? `options/?platforms=${encodeURIComponent(platformsKey)}`
            : `options/?report_type=${encodeURIComponent(reportType)}`,
        'GET', null, { signal },
    );

// A section: the same heading, blurb and spacing whichever type is chosen, so
// the form reads as one layout rather than two that happen to share a shell.
function Section({ title, hint, children, divider = true }) {
    return (
        <Box sx={{ px: 3, py: 2.5, ...(divider ? { borderBottom: '1px solid #e2e8f0' } : {}) }}>
            {title && (
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', mb: hint ? 0.5 : 1.5 }}>
                    {title}
                </Typography>
            )}
            {hint && <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 1.5 }}>{hint}</Typography>}
            {children}
        </Box>
    );
}
Section.propTypes = {
    title: PropTypes.node,
    hint: PropTypes.node,
    children: PropTypes.node,
    divider: PropTypes.bool,
};

export default function ScheduleDialog({
    open,
    initial,
    lockedKind,
    crmReportTypes,
    practiceUsers,
    clientState,
    legacyBusy,
    loading,
    onSubmitLegacy,
    loadLocations,
    ownPractices,
    onClose,
}) {
    const [form, setForm] = useState(initial);
    const [recipientInput, setRecipientInput] = useState('');
    const [advancedOpen, setAdvancedOpen] = useState(() => hasAdvancedValues(initial));
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationsError, setLocationsError] = useState('');
    // The outstanding-items list appears once the user has started, so a
    // disabled Save is never a mystery, and a pristine dialog is never a
    // wall of red. A saved schedule counts as started — its gaps are real —
    // but a new one opened with its type prefilled (the analytics tables
    // deep-link here) is still pristine until the first edit.
    const [touched, setTouched] = useState(Boolean(initial.id));

    // The host replaces `initial` when a record it fetched arrives, so the
    // dialog follows it. Comparing by identity keeps a keystroke from being
    // overwritten by the same object on an unrelated re-render.
    const seen = useRef(initial);
    useEffect(() => {
        if (seen.current === initial) return;
        seen.current = initial;
        setForm(initial);
        setRecipientInput('');
        setTouched(Boolean(initial.id));
        if (hasAdvancedValues(initial)) setAdvancedOpen(true);
    }, [initial]);

    const groups = useMemo(() => scheduleTypeGroups(crmReportTypes), [crmReportTypes]);
    const kind = lockedKind || kindOf(form.report_type);
    const isClient = Boolean(form.report_type) && kind === CLIENT;
    const editing = Boolean(form.id);
    const busy = Boolean(legacyBusy) || Boolean(clientState?.busy);
    // `loading` is the host still fetching the record behind an edit: the
    // fields and Save wait for it, but Cancel and the X stay live so a slow
    // or failed request never traps the user in the dialog.
    const locked = busy || Boolean(loading);

    // Locations and their platform availability are a client-report concern,
    // so the request only happens once a client type is the chosen type.
    const platformsKey = form.platforms.join(',');
    useEffect(() => {
        if (!isClient) return undefined;
        let valid = true;
        const controller = new AbortController();
        setLocationsLoading(true);
        setLocationsError('');
        loadLocations(platformsKey, controller.signal, form.report_type)
            .then((data) => {
                if (!valid) return;
                const authorised = data.practices || [];
                setLocations(authorised);
                // An unauthorised location is dropped rather than silently
                // submitted under a name the viewer cannot see.
                setForm((previous) => ({
                    ...previous,
                    practice_ids: previous.practice_ids.filter((id) =>
                        authorised.some((practice) => practice.id === id)
                    ),
                }));
            })
            .catch((error) => {
                if (!valid) return;
                // The call report needs no ad-platform authorisation: when the
                // options route cannot answer for it (older backend), offer the
                // viewer's own practices rather than blocking the form.
                if (!needsPlatforms(form.report_type) && (ownPractices || []).length) {
                    setLocations(ownPractices);
                    return;
                }
                setLocations([]);
                setLocationsError(error.message);
            })
            .finally(() => {
                if (valid) setLocationsLoading(false);
            });
        return () => {
            valid = false;
            controller.abort();
        };
    }, [isClient, platformsKey, loadLocations, form.report_type]);

    // Any edit invalidates a preview that was generated from the old values.
    const update = (keyOrPatch, value) => {
        setTouched(true);
        clientState?.clearPreview?.();
        const patch = typeof keyOrPatch === 'string' ? { [keyOrPatch]: value } : keyOrPatch;
        setForm((previous) => ({ ...previous, ...patch }));
    };

    const onPlatformsChange = (platforms) => {
        setLocations([]);
        setLocationsLoading(true);
        update({ platforms, practice_ids: [] });
    };

    // A type change moves the whole form to that family's defaults for the
    // settings the families disagree about: the monthly-only cadence of a
    // client report, and whether a brand-new schedule is live. Reading the
    // default from the kind every time is what stops the CRM form's Active
    // switch from leaking on to a client report.
    const onTypeChange = (nextType) => {
        const nextKind = kindOf(nextType);
        setForm((previous) => {
            const changed = kindOf(previous.report_type) !== nextKind || !previous.report_type;
            if (!changed) return { ...previous, report_type: nextType };
            return {
                ...previous,
                report_type: nextType,
                interval: nextKind === CLIENT ? MONTHLY_INTERVAL : null,
                day_of_the_week: null,
                is_enabled: previous.id ? previous.is_enabled : defaultEnabled(nextKind),
                additional_context: {},
                // The other family's own settings go too: a custom range set
                // on a client type must not re-open "More options" on a CRM
                // type, or come back when the user returns to a client type.
                title: '',
                period: 'previous_month',
                date_from: '',
                date_to: '',
            };
        });
        setAdvancedOpen(false);
        setTouched(true);
        clientState?.clearPreview?.();
    };

    // Typed-but-not-yet-committed text counts as a recipient. Without this a
    // user who types one address and clicks Save sends to nobody.
    const emails = useMemo(
        () => cleanRecipients([...form.recipients, ...recipientInput.split(/[;,\s]+/)]),
        [form.recipients, recipientInput]
    );
    const timezoneValid = !isClient || isValidTimezone(form.timezone);
    const formWithEmails = useMemo(() => ({ ...form, recipients: emails }), [form, emails]);
    const issues = useMemo(
        () => scheduleIssues(formWithEmails, { locationsLoading, locationsError, timezoneValid }),
        [formWithEmails, locationsLoading, locationsError, timezoneValid]
    );
    const canSave = issues.length === 0 && !locked;
    // Preview and download tolerate a custom range and need no recipients, so
    // they have their own, looser gate.
    const canPreview =
        isClient &&
        !busy &&
        !locationsLoading &&
        !locationsError &&
        form.title.trim() &&
        form.practice_ids.length > 0 &&
        (!needsPlatforms(form.report_type) || form.platforms.length > 0) &&
        form.formats.length > 0 &&
        timezoneValid &&
        (form.period !== 'custom' || (form.date_from && form.date_to && form.date_from <= form.date_to));

    const frequencies = frequencyOptionsFor(kind);
    const monthly = Number(form.interval) === MONTHLY_INTERVAL;
    const weekly = Number(form.interval) === WEEKLY_INTERVAL;

    // One primary action, worded the same for both families. The verb
    // changing with the report type undercut the point of unifying them.
    const saveLabel = editing ? 'Save Changes' : 'Create Schedule';

    const save = async () => {
        if (!canSave) {
            setTouched(true);
            return;
        }
        if (isClient) {
            const saved = await clientState.save(formWithEmails);
            // `true` tells the host a row changed, so it can refetch the
            // unified list; a plain close (Cancel, X) passes nothing.
            if (saved) onClose(true);
            return;
        }
        onSubmitLegacy(formWithEmails);
    };

    return (
        <Dialog
            open={open}
            // MUI traps focus inside the dialog and closes on Escape; the
            // close handler is withheld while a write is in flight so a
            // stray Escape cannot abandon a save in progress.
            onClose={busy ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    ...reportingFocusVisibleSx,
                    borderRadius: '20px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box sx={{ p: 3, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={24} sx={{ color: '#fff' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                                {editing ? 'Edit Schedule' : 'New Report Schedule'}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', opacity: 0.9 }}>
                                Configure automated report delivery
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} disabled={locked} aria-label="Close" sx={{ color: '#fff' }}>
                        <Iconify icon="mdi:close" width={24} />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, overflowY: 'auto', flex: 1 }}>
                {clientState?.error && isClient && (
                    <Box sx={{ px: 3, pt: 3 }}>
                        <Alert severity="error">{clientState.error}</Alert>
                    </Box>
                )}
                {locationsError && isClient && (
                    <Box sx={{ px: 3, pt: 3 }}>
                        <Alert severity="error">Locations unavailable: {locationsError}</Alert>
                    </Box>
                )}

                {/* One list, both families. The user never has to know which
                    family a report belongs to before choosing it. */}
                <Section
                    title="Report type"
                    hint={
                        editing
                            ? 'The report type cannot be changed after a schedule is created.'
                            : 'Choose the report to send. Everything below adapts to it.'
                    }
                >
                    <TextField
                        select
                        fullWidth
                        label="Report type"
                        value={form.report_type || ''}
                        disabled={editing || locked}
                        onChange={(event) => onTypeChange(event.target.value)}
                        SelectProps={{ native: false }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    >
                        {groups.flatMap((group) => [
                            <ListSubheader key={`${group.kind}-head`} sx={{ fontWeight: 700, color: '#475569' }}>
                                {group.label}
                            </ListSubheader>,
                            ...group.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Iconify
                                            icon={
                                                option.kind === CLIENT
                                                    ? 'mdi:chart-box-outline'
                                                    : 'mdi:file-document-outline'
                                            }
                                            width={20}
                                            sx={{ color: '#6366f1' }}
                                        />
                                        <Typography sx={{ textTransform: 'capitalize' }}>{option.label}</Typography>
                                    </Box>
                                </MenuItem>
                            )),
                        ])}
                    </TextField>
                </Section>

                <Section
                    title="When it runs"
                    hint={
                        isClient
                            ? 'Client reports are delivered monthly.'
                            : 'How often should this report be sent?'
                    }
                >
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5}>
                            {frequencies.map((frequency) => {
                                const selected = Number(form.interval) === frequency.value;
                                return (
                                    <Box
                                        key={frequency.value}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={frequency.label}
                                        aria-pressed={selected}
                                        onClick={() => update('interval', frequency.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                update('interval', frequency.value);
                                            }
                                        }}
                                        sx={{
                                            flex: frequencies.length > 1 ? 1 : '0 0 170px',
                                            minWidth: 0,
                                            p: 2,
                                            borderRadius: '12px',
                                            border: '2px solid',
                                            borderColor: selected ? frequency.color : '#e2e8f0',
                                            bgcolor: selected ? alpha(frequency.color, 0.05) : '#fff',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            textAlign: 'center',
                                            '&:hover': {
                                                borderColor: frequency.color,
                                                bgcolor: alpha(frequency.color, 0.05),
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '10px',
                                                bgcolor: alpha(frequency.color, 0.12),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 1,
                                            }}
                                        >
                                            <Iconify icon={frequency.icon} width={22} sx={{ color: frequency.color }} />
                                        </Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                                            {frequency.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>

                        {/* Weekly reveals the day; monthly reveals the day of
                            the month, but only on the family whose endpoint
                            stores one. A CRM monthly schedule has no
                            day-of-month field, and offering a control the
                            save would drop would be a lie. */}
                        {!isClient && weekly && (
                            <Box>
                                <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 1 }}>
                                    Which day should it be delivered on?
                                </Typography>
                                <ToggleButtonGroup
                                    value={form.day_of_the_week}
                                    exclusive
                                    // Exclusive groups report null when the selected day is
                                    // clicked again; a weekly schedule keeps its day instead.
                                    onChange={(event, value) => {
                                        if (value !== null) update('day_of_the_week', value);
                                    }}
                                    size="small"
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        '& .MuiToggleButton-root': {
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '10px !important',
                                            px: 2,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&.Mui-selected': {
                                                bgcolor: '#6366f1',
                                                color: '#fff',
                                                borderColor: '#6366f1',
                                                '&:hover': { bgcolor: '#4f46e5' },
                                            },
                                        },
                                    }}
                                >
                                    {DAYS_OF_WEEK.map((day) => (
                                        <ToggleButton key={day.value} value={day.value}>
                                            {day.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        )}

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                {isClient && monthly && (
                                    <TextField
                                        label="Day of the month"
                                        type="number"
                                        value={form.delivery_day}
                                        onChange={(event) => update('delivery_day', event.target.value)}
                                        disabled={locked}
                                        inputProps={{ min: 1, max: 31 }}
                                        helperText="Day 31 uses the last day of shorter months."
                                        fullWidth
                                    />
                                )}
                                <TimePicker
                                    label="Delivery time"
                                    ampm={false}
                                    value={form.time_of_day ? dayjs(`2000-01-01T${form.time_of_day}`) : null}
                                    disabled={locked}
                                    onChange={(date) =>
                                        update('time_of_day', date?.isValid() ? date.format('HH:mm') : '')
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            placeholder: 'e.g. 09:00',
                                            sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } },
                                        },
                                    }}
                                />
                                {isClient ? (
                                    <TimezoneSelect
                                        value={form.timezone}
                                        onChange={(value) => update('timezone', value)}
                                        disabled={locked}
                                    />
                                ) : null}
                            </Stack>
                        </LocalizationProvider>
                        {!isClient && (
                            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                                Delivery times for CRM reports are in {DEFAULT_TIMEZONE}.
                            </Typography>
                        )}
                    </Stack>
                </Section>

                {/* The middle section, and the only part that swaps. */}
                {isClient && (
                    <Section title="Report settings" hint="What the document covers and which files are attached.">
                        <ScheduleClientFields
                            form={form}
                            update={update}
                            onPlatformsChange={onPlatformsChange}
                            showPlatforms={needsPlatforms(form.report_type)}
                            busy={locked}
                            locations={locations}
                            locationsLoading={locationsLoading}
                            locationsError={locationsError}
                        />
                        {/* These act on the settings above without creating
                            anything, so they sit with the fields they read
                            rather than competing with Create Schedule. */}
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 2, flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}
                        >
                            <Button
                                size="small"
                                disabled={!canPreview}
                                onClick={() => clientState.preview(form)}
                                startIcon={<Iconify icon="mdi:table-eye" width={18} />}
                                sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                            >
                                Preview data
                            </Button>
                            <Button
                                size="small"
                                disabled={!canPreview}
                                onClick={() => clientState.generate(form)}
                                startIcon={<Iconify icon="mdi:file-download-outline" width={18} />}
                                sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                            >
                                Generate downloads
                            </Button>
                            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                                Neither creates a schedule.
                            </Typography>
                        </Stack>
                    </Section>
                )}

                <Section title="Recipients" hint="Type or paste email addresses — press Enter to add each one.">
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={form.recipients}
                        inputValue={recipientInput}
                        onInputChange={(event, value) => setRecipientInput(value)}
                        onChange={(event, value) => update('recipients', cleanRecipients(value))}
                        disabled={locked}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const valid = EMAIL_PATTERN.test(option);
                                return (
                                    <Chip
                                        key={option}
                                        label={option}
                                        size="small"
                                        {...getTagProps({ index })}
                                        sx={{
                                            bgcolor: valid ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                                            color: valid ? '#10b981' : '#ef4444',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            // An address is not a label; the
                                            // app's capitalising rule must not
                                            // reach it.
                                            textTransform: 'none',
                                        }}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Email recipients"
                                placeholder="e.g. john@example.com"
                                helperText="Up to 20 unique email addresses."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                        )}
                    />
                </Section>

                <Section title="Status" hint={null}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: '12px',
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '10px',
                                    bgcolor: form.is_enabled ? alpha('#10b981', 0.12) : alpha('#64748b', 0.12),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Iconify
                                    icon={form.is_enabled ? 'mdi:check-circle' : 'mdi:pause-circle'}
                                    width={22}
                                    sx={{ color: form.is_enabled ? '#10b981' : '#64748b' }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                                    {form.is_enabled ? 'Schedule Active' : 'Schedule Paused'}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                                    {form.is_enabled ? 'Reports will be sent automatically' : 'Reports will not be sent'}
                                </Typography>
                            </Box>
                        </Box>
                        <Switch
                            checked={form.is_enabled}
                            disabled={locked || (isClient && form.period === 'custom')}
                            inputProps={{ 'aria-label': 'Schedule active' }}
                            onChange={(event) => update('is_enabled', event.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#10b981',
                                },
                            }}
                        />
                    </Box>
                </Section>

                {/* Rarely needed, so out of the way — but still here, and
                    opened for you when a saved schedule uses it. */}
                {Boolean(form.report_type) && (
                    <Section title={null} hint={null} divider={false}>
                        <Button
                            onClick={() => setAdvancedOpen((previous) => !previous)}
                            aria-expanded={advancedOpen}
                            startIcon={<Iconify icon={advancedOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} />}
                            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', px: 0 }}
                        >
                            More options
                        </Button>
                        <Collapse in={advancedOpen} unmountOnExit>
                            <Box sx={{ pt: 2 }}>
                                <ScheduleAdvancedFields
                                    kind={kind}
                                    form={form}
                                    update={update}
                                    busy={locked}
                                    practiceUsers={practiceUsers}
                                />
                            </Box>
                        </Collapse>
                    </Section>
                )}

                {isClient && (clientState?.snapshot || clientState?.run) && (
                    <Section title="Preview" hint={null} divider={false}>
                        <ClientReportPreview snapshot={clientState.snapshot} />
                        {clientState.run && (
                            <Alert
                                sx={{ mt: 2 }}
                                severity={
                                    ['blocked', 'generation_failed'].includes(clientState.run.status)
                                        ? 'warning'
                                        : 'info'
                                }
                            >
                                Download generation: {clientState.run.status}.
                            </Alert>
                        )}
                        {clientState.run?.artifacts?.map((artifact) => (
                            <Button
                                key={artifact.id}
                                disabled={locked}
                                onClick={() => clientState.download(clientState.run, artifact)}
                            >
                                Download {artifact.format}
                            </Button>
                        ))}
                    </Section>
                )}
            </DialogContent>

            {/* A silently disabled Save is a mystery. The reasons are listed
                once the user has asked to save, or as soon as a chosen type
                leaves something outstanding. */}
            {touched && issues.length > 0 && (
                <Box
                    sx={{
                        mx: 3,
                        mb: 2,
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: alpha('#ef4444', 0.08),
                        border: '1px solid',
                        borderColor: alpha('#ef4444', 0.3),
                    }}
                >
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#dc2626', mb: 0.5 }}>
                        Before you can save:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#dc2626', fontSize: '12px' }}>
                        {issues.map((issue) => (
                            <li key={issue}>{issue}</li>
                        ))}
                    </Box>
                </Box>
            )}

            <Divider />

            {/* On a phone the actions stack and fill the width rather than
                wrapping into a ragged three rows. */}
            <Box
                sx={{
                    p: 2.5,
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    justifyContent: 'flex-end',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    disabled={busy}
                    onClick={onClose}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', px: 3 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!canSave}
                    onClick={save}
                    startIcon={<Iconify icon={editing ? 'mdi:check' : 'mdi:plus'} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#6366f1',
                        '&:hover': { bgcolor: '#4f46e5' },
                        px: 3,
                        borderRadius: '10px',
                    }}
                >
                    {saveLabel}
                </Button>
            </Box>
        </Dialog>
    );
}

ScheduleDialog.propTypes = {
    open: PropTypes.bool,
    initial: PropTypes.object.isRequired,
    // Set on edit: the two families post to different endpoints, so changing
    // the type of a saved schedule is meaningless.
    lockedKind: PropTypes.oneOf([LEGACY, CLIENT]),
    crmReportTypes: PropTypes.array,
    practiceUsers: PropTypes.array,
    clientState: PropTypes.object,
    legacyBusy: PropTypes.bool,
    onSubmitLegacy: PropTypes.func,
    // The authorised-location lookup, injected so the dialog can be rendered
    // without reaching the network.
    loadLocations: PropTypes.func,
    // The viewer's own practices [{id, name}]: the call report's locations when
    // the options route cannot answer for it yet. Hosts pass it from redux.
    ownPractices: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

ScheduleDialog.defaultProps = {
    loading: false,
    open: true,
    lockedKind: null,
    crmReportTypes: [],
    practiceUsers: [],
    onSubmitLegacy: () => {},
    loadLocations: fetchAuthorisedLocations,
    ownPractices: [],
};

export { emptyScheduleForm };
